import { useState } from "react";
import {
    Box,
    Button,
    Typography,
    useTheme,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    TextField,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { tokens } from "../../../theme";
import Header from "../global/Header";
import useAuth from "../../../hooks/useAuth";
import {
    AddCircleOutline,
    LockReset,
    Block,
    CheckCircleOutline,
    DeleteOutline,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import PulseLoader from "react-spinners/PulseLoader";
import {
    useGetTechsQuery,
    useDisableTechMutation,
    useEnableTechMutation,
    useDeleteTechMutation,
    useSetTechPasswordMutation,
    // useSetTechMfaMutation,
} from "../../../features/techs/techsApiSlice";
import NewTechLoginForm from "./NewTechLoginForm";

const Techs = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { status } = useAuth();
    const navigate = useNavigate();
    const isAdmin = status === "Admin";

    // Queries & mutations
    const {
        data: techRows = [],
        isLoading,
        isError,
    } = useGetTechsQuery();

    const [disableTech] = useDisableTechMutation();
    const [enableTech] = useEnableTechMutation();
    const [deleteTech] = useDeleteTechMutation();
    const [setTechPassword] = useSetTechPasswordMutation();

    // Dialog state
    const [openToggleDialog, setOpenToggleDialog] = useState(false);
    const [openResetDialog, setOpenResetDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [openNew, setOpenNew] = useState(false);

    const [selectedTech, setSelectedTech] = useState(null);
    const [newPassword, setNewPassword] = useState("");

    const onRowClick = (params) => {
        navigate(`/console/techs/${params.row.username}`);
    };

    const handleOpenToggle = (tech) => {
        setSelectedTech(tech);
        setOpenToggleDialog(true);
    };
    const handleCloseToggle = () => setOpenToggleDialog(false);

    const handleConfirmToggle = async () => {
        if (!selectedTech) return;
        const fn = selectedTech.enabled ? disableTech : enableTech;
        await fn(selectedTech.username);
        setOpenToggleDialog(false);
        setSelectedTech(null);
    };

    const handleOpenReset = (tech) => {
        setSelectedTech(tech);
        setNewPassword("");
        setOpenResetDialog(true);
    };
    const handleCloseReset = () => setOpenResetDialog(false);

    const handleConfirmReset = async () => {
        if (!selectedTech || !newPassword) return;
        await setTechPassword({ username: selectedTech.username, password: newPassword });
        setOpenResetDialog(false);
        setSelectedTech(null);
    };

    const handleOpenDelete = (tech) => {
        setSelectedTech(tech);
        setOpenDeleteDialog(true);
    };
    const handleCloseDelete = () => setOpenDeleteDialog(false);

    const handleConfirmDelete = async () => {
        if (!selectedTech) return;
        await deleteTech(selectedTech.username);
        setOpenDeleteDialog(false);
        setSelectedTech(null);
    };

    if (!isAdmin) {
        return (
            <Box m="20px">
                <Header title="US FUEL TECHS" subtitle="Admin only" />
                <Typography>You do not have access to this page.</Typography>
            </Box>
        );
    }

    if (isLoading) return <PulseLoader color="#FFF" />;

    if (isError) {
        return (
            <Box m="20px">
                <Header title="US FUEL TECHS" subtitle="Manage installer accounts" />
                <Typography color="error.main">Failed to load techs.</Typography>
            </Box>
        );
    }

    // Plain const (not a hook) — safe with early returns
    const rows = (techRows || []).map((r) => ({
        id: r.id || r.username,
        ...r,
    }));

    const columns = [
        { field: "username", headerName: "Username", flex: 1 },
        { field: "name", headerName: "Name", flex: 1 },
        { field: "email", headerName: "Email", flex: 1 },
        { field: "status", headerName: "Status", width: 130 },
        { field: "mfa", headerName: "MFA", width: 100 },
        {
            field: "enabled",
            headerName: "Enabled",
            width: 110,
            valueFormatter: (p) => (p.value ? "Yes" : "No"),
        },
        { field: "created_at", headerName: "Created", width: 160 },
        {
            field: "actions",
            headerName: "Actions",
            flex: 1,
            sortable: false,
            renderCell: (params) => {
                const tech = params.row;
                return (
                    <Box display="flex" gap={1}>
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<LockReset />}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleOpenReset(tech);
                            }}
                        >
                            Reset Password
                        </Button>
                        <Button
                            variant={tech.enabled ? "outlined" : "contained"}
                            color={tech.enabled ? "error" : "success"}
                            size="small"
                            startIcon={tech.enabled ? <Block /> : <CheckCircleOutline />}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleOpenToggle(tech);
                            }}
                        >
                            {tech.enabled ? "Disable" : "Enable"}
                        </Button>
                        <Button
                            variant="text"
                            color="error"
                            size="small"
                            startIcon={<DeleteOutline />}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleOpenDelete(tech);
                            }}
                        >
                            Delete
                        </Button>
                    </Box>
                );
            },
        },
    ];

    return (
        <Box m="20px">
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Header title="US FUEL TECHS" subtitle="Manage installer logins for the sister software" />
                <Button
                    variant="contained"
                    startIcon={<AddCircleOutline />}
                    onClick={() => setOpenNew(true)}
                >
                    Create Tech
                </Button>
            </Box>

            <Box
                mt="20px"
                height="75vh"
                sx={{
                    "& .MuiDataGrid-root": { border: "none" },
                    "& .MuiDataGrid-columnHeaders": { backgroundColor: colors.blueAccent[700], borderBottom: "none" },
                    "& .MuiDataGrid-virtualScroller": { backgroundColor: colors.primary[400] },
                    "& .MuiDataGrid-footerContainer": { borderTop: "none", backgroundColor: colors.blueAccent[700] },
                }}
            >
                <DataGrid rows={rows} columns={columns} onRowClick={onRowClick} />
            </Box>

            {/* Enable/Disable dialog */}
            <Dialog open={openToggleDialog} onClose={handleCloseToggle}>
                <DialogTitle>{selectedTech?.enabled ? "Disable Tech" : "Enable Tech"}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {selectedTech?.enabled
                            ? `Disable ${selectedTech?.username}? They will lose access immediately.`
                            : `Enable ${selectedTech?.username}? They will regain access immediately.`}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseToggle} color="inherit" variant="outlined">
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmToggle} color={selectedTech?.enabled ? "error" : "success"} variant="contained">
                        {selectedTech?.enabled ? "Disable" : "Enable"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Reset password dialog */}
            <Dialog open={openResetDialog} onClose={handleCloseReset}>
                <DialogTitle>Reset Password</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Set a new permanent password for <b>{selectedTech?.username}</b>.
                    </DialogContentText>
                    <Box mt={2}>
                        <TextField
                            fullWidth
                            label="New password"
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="Aa1!StrongPass"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseReset} color="inherit" variant="outlined">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmReset}
                        color="primary"
                        variant="contained"
                        disabled={!newPassword}
                    >
                        Set Password
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete dialog */}
            <Dialog open={openDeleteDialog} onClose={handleCloseDelete}>
                <DialogTitle>Delete Tech</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Permanently delete <b>{selectedTech?.username}</b>? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDelete} color="inherit" variant="outlined">
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmDelete} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
            {/* New Tech dialog */}
            <NewTechLoginForm open={openNew} onClose={() => setOpenNew(false)} />
        </Box>
    );
};

export default Techs;
